import React, { useState, useEffect } from 'react';
import { X, User, Mail, Shield, Calendar, Search, KeyRound, Edit, Trash2 } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import axios from 'axios';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import PasswordResetLinkModal from './PasswordResetLinkModal';
import EditUserModal from './EditUserModal';

interface User {
  uid: string;
  email: string;
  name: string;
  lastname: string;
  user_type: 'user' | 'admin' | 'superadmin';
  created_at: any;
}

interface UsersModalProps {
  client: {
    uid: string;
    name: string;
    display_name?: string;
  };
  onClose: () => void;
  inline?: boolean;
}

const UsersModal: React.FC<UsersModalProps> = ({ client, onClose, inline = false }) => {
  const { userData, currentUser } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'superadmin' | 'admin' | 'user'>('all');

  // Password reset modal state
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  // T-E: edición y borrado desde Config→Usuarios (solo superadmin).
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditModalOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) return;
    try {
      // Approach REST unificado (mismo endpoint que la gestión global de usuarios).
      const token = await currentUser?.getIdToken();
      await axios.delete(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      alert(error?.response?.data?.error || 'Error al eliminar el usuario');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [client.uid]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Fetch real users from Firestore that have this client assigned
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('assigned_clients', 'array-contains', client.uid));
      const snapshot = await getDocs(q);

      const realUsers: User[] = [];
      snapshot.forEach((doc) => {
        const userData = doc.data();
        realUsers.push({
          uid: doc.id,
          email: userData.email || '',
          name: userData.name || '',
          lastname: userData.lastname || '',
          user_type: userData.user_type || 'user',
          created_at: userData.created_at
        });
      });

      setUsers(realUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeColor = (type: string) => {
    switch(type) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUserTypeLabel = (type: string) => {
    switch(type) {
      case 'superadmin':
        return 'Super Admin';
      case 'admin':
        return 'Administrador';
      default:
        return 'Usuario';
    }
  };

  // Sort users by type (superadmin > admin > user) and then by name
  const sortUsers = (usersToSort: User[]) => {
    const typeOrder = { superadmin: 0, admin: 1, user: 2 };
    return usersToSort.sort((a, b) => {
      const typeCompare = typeOrder[a.user_type] - typeOrder[b.user_type];
      if (typeCompare !== 0) return typeCompare;

      const nameA = `${a.name} ${a.lastname}`.toLowerCase();
      const nameB = `${b.name} ${b.lastname}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Filter and search users
  const filteredUsers = sortUsers(users.filter(user => {
    // Filter by type
    if (filterType !== 'all' && user.user_type !== filterType) {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${user.name} ${user.lastname}`.toLowerCase();
      const email = user.email.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    }

    return true;
  }));

  return (
    <div className={inline
      ? 'w-full'
      : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'
    }>
      <div className={inline
        ? 'bg-white rounded-lg w-full'
        : 'bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden'
      }>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Usuarios de {client.display_name || client.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredUsers.length} de {users.length} usuario{users.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as typeof filterType)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Todos los tipos</option>
              <option value="superadmin">Super Admin</option>
              <option value="admin">Administrador</option>
              <option value="user">Usuario</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="space-y-4">
              {filteredUsers.map((user) => (
                <div key={user.uid} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {user.name} {user.lastname}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <p className="text-sm text-gray-500">
                            Creado: {user.created_at?.toDate ? user.created_at.toDate().toLocaleDateString() :
                                    user.created_at?.seconds ? new Date(user.created_at.seconds * 1000).toLocaleDateString() :
                                    'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUserTypeColor(user.user_type)}`}>
                        <div className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {getUserTypeLabel(user.user_type)}
                        </div>
                      </span>
                      {isSuperadmin && (
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-1.5 rounded text-blue-600 border border-blue-200 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer"
                          title="Editar usuario"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isSuperadmin && (
                        <button
                          onClick={() => setResetTarget(user)}
                          className="px-2 py-1 text-xs font-medium border border-amber-300 rounded text-amber-700 hover:bg-amber-50 transition-colors flex items-center gap-1 cursor-pointer"
                          title="Generar link de reset de password"
                        >
                          <KeyRound className="w-3 h-3" />
                          Reset password
                        </button>
                      )}
                      {isSuperadmin && (
                        <button
                          onClick={() => handleDeleteUser(user.uid)}
                          className="p-1.5 rounded text-red-600 border border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors cursor-pointer"
                          title="Eliminar usuario"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {users.length > 0 ? 'No se encontraron coincidencias' : 'No hay usuarios'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {users.length > 0
                  ? 'Intenta con otros términos de búsqueda o filtros'
                  : 'No se encontraron usuarios para este cliente'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Password Reset Modal (reusable) */}
      <PasswordResetLinkModal
        user={resetTarget}
        onClose={() => setResetTarget(null)}
      />

      {/* Edit User Modal (reusable) — T-E */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingUser(null);
          }}
          onSave={() => {
            fetchUsers();
            setEditModalOpen(false);
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default UsersModal;
